import { unref, onUnmounted } from 'vue';
import { useList } from '@/voc/use/use-list.js';

export function useDrawers (storeId = 'pendo') {
    const {
        subscriberUuid,
        item: getDrawer,
        items: getDrawers,
        addItem: addDrawer,
        updateItem: updateDrawer,
    } = useList(`${storeId}Drawers`);

    const registerDrawer = (
        drawer,
        { emits = [], rendered = false, visible = false } = { emits: [], rendered: false, visible: false },
    ) => {
        addDrawer({
            id: drawer,
            rendered,
            visible,
            metadata: {},
            emits: ['onOpen', 'onClose', ...emits],
            callbacks: new Map(),
        });
    };

    const registerDrawers = (drawers = []) => {
        drawers.forEach((drawer) => {
            registerDrawer(drawer.drawer, drawer.options);
        });
    };

    const openDrawer = ({ drawer, metadata }) => {
        updateDrawer(drawer, {
            rendered: true,
            visible: true,
            ...(metadata && { metadata: { ...metadata } }),
        });
        emit(drawer, 'onOpen');
    };

    const closeDrawer = ({ drawer }) => {
        if (getDrawer(drawer).visible) {
            updateDrawer(drawer, {
                visible: false,
                metadata: {},
            });
            emit(drawer, 'onClose');
        }
    };

    const closeAllDrawers = () => {
        unref(getDrawers).forEach(({ id: drawer }) => {
            closeDrawer({ drawer });
        });
    };

    function emit (drawer, event, data) {
        if (getDrawer(drawer).callbacks.has(event)) {
            getDrawer(drawer)
                .callbacks.get(event)
                .forEach((callback) => callback(data));
        }
    }

    const emits = (drawer) => {
        const currentDrawer = getDrawer(drawer);

        const newEmits = currentDrawer.emits.reduce((emits, emit) => {
            emits[emit] = (callback) => {
                if (!currentDrawer.callbacks.has(emit)) {
                    currentDrawer.callbacks.set(emit, new Map());
                }
                currentDrawer.callbacks.get(emit).set(subscriberUuid, callback);

                // Immediate callback for 'onOpen' if drawer is already visible
                if (emit === 'onOpen' && currentDrawer.visible) {
                    callback();
                }
            };

            return emits;
        }, {});

        return newEmits;
    };

    function clearCallbacks () {
        if (subscriberUuid) {
            unref(getDrawers).forEach((drawer) => {
                drawer.callbacks.forEach((callback) => {
                    callback.delete(subscriberUuid);
                });
            });
        }
    }

    onUnmounted(() => {
        clearCallbacks();
    });

    return {
        clearCallbacks,
        closeAllDrawers,
        closeDrawer,
        emit,
        emits,
        getDrawer,
        openDrawer,
        registerDrawer,
        registerDrawers,
        updateDrawer,
    };
}
